
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface QuizScore {
  moduleName: string;
  percentage: number;
  passed: boolean;
  attemptDate: string;
  score: number;
  maxScore: number;
}

interface MarkSheetGeneratorProps {
  userName: string;
  courseTitle: string;
  quizScores: QuizScore[];
  overallAverage: number;
  certificateNumber: string;
  completionDate: string;
}

const MarkSheetGenerator: React.FC<MarkSheetGeneratorProps> = ({
  userName,
  courseTitle,
  quizScores,
  overallAverage,
  certificateNumber,
  completionDate
}) => {
  const passedQuizzes = quizScores.filter(quiz => quiz.passed).length;
  const totalQuizzes = quizScores.length;

  return (
    <div className="mark-sheet w-full max-w-4xl mx-auto bg-white shadow-lg print:shadow-none">
      {/* Header */}
      <Card className="mb-6">
        <CardHeader className="text-center bg-gradient-to-r from-blue-50 to-purple-50">
          <CardTitle className="text-2xl font-bold text-gray-800">
            Academic Transcript
          </CardTitle>
          <p className="text-gray-600">NeoCertify Verified Mark Sheet</p>
        </CardHeader>
        
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Student Information</h3>
              <p><span className="font-medium">Name:</span> {userName}</p>
              <p><span className="font-medium">Course:</span> {courseTitle}</p>
              <p><span className="font-medium">Completion Date:</span> {completionDate}</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Certificate Details</h3>
              <p><span className="font-medium">Certificate Number:</span> {certificateNumber}</p>
              <p><span className="font-medium">Overall Average:</span> 
                <span className={`ml-2 font-bold ${overallAverage >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                  {overallAverage.toFixed(1)}%
                </span>
              </p>
              <p><span className="font-medium">Modules Passed:</span> {passedQuizzes}/{totalQuizzes}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quiz Scores Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Module Assessment Results
            <Badge variant={passedQuizzes === totalQuizzes ? "default" : "secondary"}>
              {passedQuizzes === totalQuizzes ? "All Passed" : `${passedQuizzes}/${totalQuizzes} Passed`}
            </Badge>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Module</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Score</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Max Score</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Percentage</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Status</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {quizScores.map((quiz, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-4 py-3 font-medium">
                      {quiz.moduleName}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {quiz.score}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {quiz.maxScore}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      <span className={`font-semibold ${quiz.percentage >= 85 ? 'text-green-600' : 'text-red-600'}`}>
                        {quiz.percentage.toFixed(1)}%
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {quiz.passed ? (
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Pass</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1 text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Fail</span>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center text-sm">
                      {quiz.attemptDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Assessment Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">Minimum Required:</span>
                <p className="text-blue-700">85% per module, 80% overall</p>
              </div>
              <div>
                <span className="font-medium">Achievement:</span>
                <p className={overallAverage >= 80 ? "text-green-700" : "text-orange-700"}>
                  {overallAverage.toFixed(1)}% Overall Average
                </p>
              </div>
              <div>
                <span className="font-medium">Status:</span>
                <p className={passedQuizzes === totalQuizzes && overallAverage >= 80 ? "text-green-700" : "text-orange-700"}>
                  {passedQuizzes === totalQuizzes && overallAverage >= 80 ? "Certificate Eligible" : "Requirements Not Met"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>This mark sheet is generated automatically and verified through NeoCertify system.</p>
        <p>For verification, visit verify.neoprismacademy.com with certificate number {certificateNumber}</p>
      </div>
    </div>
  );
};

export default MarkSheetGenerator;
